import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { collection, onSnapshot, orderBy, query, getFirestore } from 'firebase/firestore';
import { authFetch } from '../../../utils/cloudFunctionsAuth';
import { RepeatingInvoice } from '../../../types/accounting';

const BASE = 'https://us-central1-web-construct-app.cloudfunctions.net';
const REPEATING = 'repeating_invoices';

interface RepeatingInvoiceContextType {
  repeatingInvoices: RepeatingInvoice[];
  loading: boolean;
  actionLoading: string | null;
  fetchRepeatingInvoices: () => void;
  createRepeatingInvoice: (payload: CreateRepeatingPayload) => Promise<void>;
  updateRepeatingInvoice: (id: string, payload: UpdateRepeatingPayload) => Promise<void>;
  deleteRepeatingInvoice: (id: string) => Promise<void>;
  /** Generate a single invoice/bill NOW from the template, dated today,
   *  without advancing the recurring `nextDate`. Returns the created
   *  doc's id + number on success so callers can navigate / toast. */
  manuallyGenerate: (id: string) => Promise<ManuallyGenerateResult>;
}

export interface ManuallyGenerateResult {
  type: 'invoice' | 'bill';
  docId: string;
  number: string;
  total: number;
  date: string;
}

interface CreateRepeatingPayload {
  contactId: string;
  contactName: string;
  templateType?: 'invoice' | 'bill';
  lineAmountTypes?: 'Exclusive' | 'Inclusive' | 'NoTax';
  frequency: string;
  startDate?: string;
  endDate?: string;
  reference?: string;
  status?: string;
  lineItems: Array<{ description: string; quantity: number; unitAmount: number; taxType?: string }>;
}

interface UpdateRepeatingPayload {
  templateType?: 'invoice' | 'bill';
  lineAmountTypes?: 'Exclusive' | 'Inclusive' | 'NoTax';
  frequency?: string;
  startDate?: string;
  endDate?: string;
  nextDate?: string;
  reference?: string;
  status?: string;
  autoEmailDraft?: boolean;
  lineItems?: Array<{ description: string; quantity: number; unitAmount: number; taxType?: string }>;
}

const RepeatingInvoiceContext = createContext<RepeatingInvoiceContextType | null>(null);

export function useRepeatingInvoice(): RepeatingInvoiceContextType {
  const ctx = useContext(RepeatingInvoiceContext);
  if (!ctx) throw new Error('useRepeatingInvoice must be used within RepeatingInvoiceProvider');
  return ctx;
}

export function RepeatingInvoiceProvider({ children }: { children: ReactNode }) {
  const [repeatingInvoices, setRepeatingInvoices] = useState<RepeatingInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Real-time listener — updates the list instantly on any Firestore change
  useEffect(() => {
    const db = getFirestore();
    const q = query(collection(db, REPEATING), orderBy('nextDate', 'asc'));
    const unsub = onSnapshot(q, (snap) => {
      setRepeatingInvoices(snap.docs.map(d => ({ id: d.id, ...d.data() } as RepeatingInvoice)));
      setLoading(false);
    }, (err) => {
      console.error('[RepeatingInvoiceContext] onSnapshot error', err);
      setLoading(false);
    });
    return unsub;
  }, []);

  // Kept for manual refresh compatibility (no-op since onSnapshot handles updates)
  const fetchRepeatingInvoices = useCallback(() => {}, []);

  const createRepeatingInvoice = useCallback(async (payload: CreateRepeatingPayload) => {
    setActionLoading('create');
    try {
      const resp = await authFetch(`${BASE}/createRepeatingInvoiceHttp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await resp.json();
      if (!data.ok) throw new Error(data.error || 'Create failed');
      // onSnapshot will pick up the new document automatically
    } finally {
      setActionLoading(null);
    }
  }, []);

  const updateRepeatingInvoice = useCallback(async (id: string, payload: UpdateRepeatingPayload) => {
    setActionLoading('update');
    try {
      const resp = await authFetch(`${BASE}/updateRepeatingInvoiceHttp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ repeatingInvoiceId: id, ...payload }),
      });
      const data = await resp.json();
      if (!data.ok) throw new Error(data.error || 'Update failed');
      // onSnapshot will reflect the update automatically
    } finally {
      setActionLoading(null);
    }
  }, []);

  const deleteRepeatingInvoice = useCallback(async (id: string) => {
    setActionLoading('delete');
    try {
      const resp = await authFetch(`${BASE}/deleteRepeatingInvoiceHttp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ repeatingInvoiceId: id }),
      });
      const data = await resp.json();
      if (!data.ok) throw new Error(data.error || 'Delete failed');
      // onSnapshot will remove the deleted document automatically
    } finally {
      setActionLoading(null);
    }
  }, []);

  // Calls manuallyGenerateFromRepeatingHttp — creates one invoice/bill
  // with today's date, doesn't touch the recurring schedule.
  const manuallyGenerate = useCallback(async (id: string): Promise<ManuallyGenerateResult> => {
    setActionLoading('generate');
    try {
      const resp = await authFetch(`${BASE}/manuallyGenerateFromRepeatingHttp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ repeatingInvoiceId: id }),
      });
      const data = await resp.json();
      if (!data?.ok) throw new Error(data?.error || 'Generate failed');
      return {
        type:   data.type,
        docId:  String(data.docId),
        number: String(data.number),
        total:  Number(data.total) || 0,
        date:   String(data.date),
      };
    } finally {
      setActionLoading(null);
    }
  }, []);

  return (
    <RepeatingInvoiceContext.Provider
      value={{
        repeatingInvoices,
        loading,
        actionLoading,
        fetchRepeatingInvoices,
        createRepeatingInvoice,
        updateRepeatingInvoice,
        deleteRepeatingInvoice,
        manuallyGenerate,
      }}
    >
      {children}
    </RepeatingInvoiceContext.Provider>
  );
}
